import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Check, Cpu, Users, Sparkles, MessageSquare } from 'lucide-react';
import BackButton from '../components/BackButton';
import PaymentPathModal from '../components/enterprise/PaymentPathModal';

const AI_IMG = 'https://static.prod-images.emergentagent.com/jobs/2f90fcac-783a-4f75-acaf-ca2c90f51186/images/d651792cea3f644f180451f7aa93f7aadcaf66a2e84e715a0bc786d0f21f2c4b.png';
const HUMAN_IMG = 'https://static.prod-images.emergentagent.com/jobs/2f90fcac-783a-4f75-acaf-ca2c90f51186/images/c77cb4978048d7770d7473836d7a1d8b89d7d35bb307d209deca794648cba940.png';

const container = { hidden: {}, show: { transition: { staggerChildren: 0.1 } } };
const fadeUp = { hidden: { opacity: 0, y: 16 }, show: { opacity: 1, y: 0, transition: { duration: 0.4, ease: 'easeOut' } } };

export default function EnterpriseChoice() {
  const [modalType, setModalType] = useState(null); // 'ia' | 'human' | null

  return (
    <div className="theme-light min-h-screen flex items-center justify-center p-4 pb-28 sm:pb-4">
      <motion.div variants={container} initial="hidden" animate="show" className="max-w-5xl w-full">
        <motion.div variants={fadeUp} className="mb-10">
          <BackButton />
        </motion.div>

        {/* Header */}
        <motion.div variants={fadeUp} className="text-center mb-14">
          <p className="uppercase text-xs tracking-[0.3em] lt-accent mb-4 font-body font-medium">Souscription Entreprise</p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-display font-medium lt-title tracking-tighter mb-5">
            Quel accompagnement
            <br />
            <span className="text-[#92710A]">pour votre équipe ?</span>
          </h1>
          <p className="font-body lt-subtitle text-base sm:text-lg max-w-xl mx-auto leading-relaxed">
            Deux offres distinctes. Chacune avec son propre espace RH dédié.
          </p>
        </motion.div>

        {/* Cards */}
        <div className="grid md:grid-cols-2 gap-6 lg:gap-8 max-w-4xl mx-auto">
          {/* Coaching IA */}
          <motion.button variants={fadeUp}
            onClick={() => setModalType('ia')}
            data-testid="enterprise-coaching-ia"
            className="lt-card p-0 text-left overflow-hidden group"
          >
            <div className="relative h-44 overflow-hidden">
              <img src={AI_IMG} alt="Coaching IA" className="w-full h-full object-cover opacity-70 group-hover:scale-105 transition-transform duration-500" />
              <div className="absolute inset-0 bg-gradient-to-t from-white via-white/60 to-transparent" />
              <div className="absolute bottom-4 left-6">
                <div className="w-11 h-11 rounded-xl bg-[#F5F0E3] border border-[#E8DFC8] flex items-center justify-center shadow-sm">
                  <Cpu className="w-5 h-5 text-[#92710A]" />
                </div>
              </div>
            </div>
            <div className="p-6 pt-4">
              <h2 className="text-xl font-display font-medium lt-title mb-1 group-hover:text-[#92710A] transition-colors">Coaching IA</h2>
              <p className="lt-accent text-sm font-body font-medium mb-3">Licences par collaborateur</p>
              <p className="font-body lt-subtitle text-sm leading-relaxed mb-5">Entraînement managérial avec Owen, votre coach IA vocal. Sessions illimitées, feedback instantané.</p>
              <ul className="space-y-2 mb-6">
                {['Coach IA vocal et écrit (Owen)', 'N licences selon vos besoins', 'Durée flexible par licence', 'Dashboard RH avec suivi', 'Facturation centralisée'].map((f, i) => (
                  <li key={i} className="flex items-center gap-2.5 lt-subtitle text-sm font-body">
                    <Check className="w-3.5 h-3.5 text-[#D4AF37] flex-shrink-0" /><span>{f}</span>
                  </li>
                ))}
              </ul>
              <div className="w-full py-3 lt-btn-gold font-display text-sm flex items-center justify-center gap-2">
                <Sparkles className="w-4 h-4" /> Configurer l'offre IA
              </div>
            </div>
          </motion.button>

          {/* Coaching Humain */}
          <motion.button variants={fadeUp}
            onClick={() => setModalType('human')}
            data-testid="enterprise-coaching-humain"
            className="lt-card p-0 text-left overflow-hidden group relative"
          >
            <div className="absolute top-4 right-4 z-10 bg-[#059669] text-white text-[10px] font-display font-bold px-3 py-1 rounded-full uppercase tracking-wider">
              Nouveau
            </div>
            <div className="relative h-44 overflow-hidden">
              <img src={HUMAN_IMG} alt="Coaching Humain" className="w-full h-full object-cover opacity-70 group-hover:scale-105 transition-transform duration-500" />
              <div className="absolute inset-0 bg-gradient-to-t from-white via-white/60 to-transparent" />
              <div className="absolute bottom-4 left-6">
                <div className="w-11 h-11 rounded-xl bg-[#ECFDF5] border border-[#D1FAE5] flex items-center justify-center shadow-sm">
                  <Users className="w-5 h-5 text-[#059669]" />
                </div>
              </div>
            </div>
            <div className="p-6 pt-4">
              <h2 className="text-xl font-display font-medium lt-title mb-1 group-hover:text-[#059669] transition-colors">Coaching Humain</h2>
              <p className="text-[#059669] text-sm font-body font-medium mb-3">Programme par sessions</p>
              <p className="font-body lt-subtitle text-sm leading-relaxed mb-5">Accompagnement individuel par des coachs certifiés. Sessions personnalisées avec des experts.</p>
              <ul className="space-y-2 mb-6">
                {['Coachs certifiés de l\'Espace', 'Sessions attribuées par collaborateur', 'Budget décrémenté après validation', 'Orientation IA vers le bon coach', 'Dashboard RH dédié + reporting'].map((f, i) => (
                  <li key={i} className="flex items-center gap-2.5 lt-subtitle text-sm font-body">
                    <Check className="w-3.5 h-3.5 text-[#059669] flex-shrink-0" /><span>{f}</span>
                  </li>
                ))}
              </ul>
              <div className="w-full py-3 bg-[#059669] text-white rounded-full font-display font-medium text-sm flex items-center justify-center gap-2 hover:bg-[#047857] transition-all">
                <MessageSquare className="w-4 h-4" /> Configurer l'offre Humain
              </div>
            </div>
          </motion.button>
        </div>

        <motion.p variants={fadeUp} className="text-center lt-muted text-xs mt-10 font-body">
          Chaque offre dispose de son propre Dashboard RH. Vous pouvez souscrire aux deux offres.
        </motion.p>
      </motion.div>

      <PaymentPathModal
        isOpen={!!modalType}
        offerType={modalType}
        onClose={() => setModalType(null)}
      />
    </div>
  );
}
